<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Consultants</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Consultants</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Consultant Directory</h1>
                <p class="mb-0 small">Manage and track professional service providers.</p>
              </div>
            </div>

            <div class="form-body">
              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Category:</label>
                    <select class="form-select form-select-sm" v-model="filters.category">
                      <option value="">All</option>
                      <option v-for="category in uniqueCategories" :key="category.id" :value="category.name">{{
                        category.name }}</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Registered:</label>
                    <select class="form-select form-select-sm" v-model="filters.date" disabled>
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status" disabled>
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="pending">Inactive</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <!-- <button class="btn btn-sm btn-success w-100 mb-1 justify-content-center d-flex align-items-center" @click="registerNewConsultant">
                        <span class="material-symbols-outlined align-items-center me-2">
                          person_add
                        </span>
                        Register New Consultant
                      </button> -->
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search"
                        class="form-control form-control-sm search-input d-flex w-100"
                        placeholder="Search by consultant name or address" />
                    </div>
                  </div>

                  <div class="table-controls-right">
                    <div class="export-links">
                      <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                        PDF</a>
                      <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                        Excel</a>
                    </div>

                    <div class="register-wrapper">
                      <button
                        class="btn btn-md btn-success register-btn d-flex justify-content-center align-items-center"
                        @click="registerNewConsultant">
                        <span class="material-symbols-outlined align-items-center me-2">
                          person_add
                        </span>
                        <router-link to="/dashboard/payment/register/consultant"
                          class="text-white text-decoration-none">Register New Consultant</router-link>
                      </button>
                    </div>
                  </div>

                </div>

              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading consultants...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Consultant Name</th>
                        <th>Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="3" class="text-center">No consultants found</td>
                      </tr>
                      <tr v-for="(consultant, index) in paginatedItems" :key="consultant.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no">
                          <span>{{ (currentPage - 1) * pageLength + index + 1 }}</span>
                        </td>
                        <td data-label="Consultant Name" class="primary-col fw-semibold">{{ consultant.name }}</td>
                        <td data-label="Address">{{ consultant.address || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <button class="page-link" @click.prevent="changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const consultants = ref([])
const categorys = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL

const filters = ref({
  category: '',
  date: '',
  status: ''
})

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

const fetchConsultants = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/payment/consultant/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    consultants.value = data.map(consultant => ({
      id: consultant.id,
      name: consultant.name || '',
      address: consultant.address || ''
    }))
  } catch (error) {
    console.error('Failed to fetch consultants:', error)
    consultants.value = []
  } finally {
    loading.value = false
  }
}

const fetchCategories = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/category`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    categorys.value = response.data.map(category => ({
      id: category.id,
      name: category.name,
      organisation_code: category.orgCode,
    }))
  } catch (error) {
    console.error('Failed to fetch Categories:', error)
  }
}

const uniqueCategories = computed(() => {
  return [...categorys.value].sort((a, b) => a.name.localeCompare(b.name))
})

const filteredItems = computed(() => {
  let filtered = consultants.value

  // Apply search filter
  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(consultant => {
      return (
        (consultant.name || '').toLowerCase().includes(term) ||
        (consultant.address || '').toLowerCase().includes(term)
      )
    })
  }

  // Apply MDA filter
  if (filters.value.category) {
    filtered = filtered.filter(w => w.category === filters.value.category)
  }

  // Date and status are currently not available in consultant DTO.

  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const clearFilters = () => {
  filters.value = {
    category: '',
    date: '',
    status: ''
  }
  search.value = ''
}

// const formatDate = (dateString) => {
//   const date = new Date(dateString)
//   return date.toLocaleDateString('en-US', {
//     year: 'numeric',
//     month: 'short',
//     day: 'numeric'
//   })
// }

// const formatCurrency = (amount) => {
//   return '₦' + amount.toLocaleString('en-NG')
// }

const exportToPDF = () => {
  const doc = new jsPDF()

  // Add title
  doc.setFontSize(16)
  doc.text('Generated Consultants', 14, 15)

  // Add date
  doc.setFontSize(10)
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22)

  autoTable(doc, {
    startY: 30,
    head: [['Consultant Name', 'Address']],
    body: filteredItems.value.map((consultant) => [
      consultant.name,
      consultant.address || '—'
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 139, 202] }
  })

  doc.save('consultants.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Consultants')

  // Add headers
  worksheet.addRow(['Consultant Name', 'Address'])

  // Add data
  filteredItems.value.forEach((consultant) => {
    worksheet.addRow([
      consultant.name,
      consultant.address || '—'
    ])
  })

  // Style header row
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  }

  // Auto-fit columns
  worksheet.columns.forEach(column => {
    column.width = 20
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'consultants.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  await fetchCategories()
  await fetchConsultants()
  document.title = "View Consultants"
})

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.filters-section {
  background-color:var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--table-border);
}

.form-label {
  margin-bottom: 0.25rem;
    color: var(--color);
}

.showing-info {
  padding-left: 0.5rem;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 1000px;
}

.table-controls-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.register-wrapper {
  display: flex;
  max-height: 35px;
  justify-content: flex-end;
}

.register-btn {
  width: auto;
  min-width: 240px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }

  .table-controls-right {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }

  .export-links {
    width: auto;
    display: flex;
    gap: 1rem;
  }

  .export-link {
    flex: 0 0 auto;
    text-align: center;
  }

  .register-wrapper {
    width: 100%;
    justify-content: center;
  }

  .register-btn {
    width: auto !important;
    min-width: 200px;
    max-width: 100%;
  }
}
</style>
